import { StateObservable } from 'redux-observable';
import { Observable } from 'rxjs';
import { catchError, filter, map, mergeMap } from 'rxjs/operators';

import { ICourse } from '@src/model/course/Course';
import EntityApiServiceRegistry from '@src/service/api/registry/entity/EntityApiServiceRegistry';
import ListFilterBusinessStore from '@src/service/business/common/listFilterBusinessStore';
import { ICollectionData, ICollectionFetchPayload, ILemonAction, IPayloadAction } from '@src/service/business/common/types';
import { createStaticMessageUserFeedbackError } from '@src/service/business/common/userFeedbackUtils';
import { ICourseListFilter } from '@src/service/business/courses/courseListBusinessStore';
import { actionThunk } from '@src/service/util/observable/operators';
import { startGlobalProgress, stopGlobalProgress } from '@src/service/util/observable/operators';
import { reportCaughtMessage } from '@src/service/util/observable/operators/userFeedback';

// -
// -------------------- Types&Consts

// additional and private course list endpoints share the same model thus filter can be reused
export interface IAdditionalCourseListFilter extends ICourseListFilter {}

// List filter ID
const ADDITIONAL_COURSE_LIST_FILTER = '@@ADDITIONAL_COURSE_LIST_FILTER';

// -
// -------------------- Selectors

/** Returns list of courses from store. */
const getList = (store: any): ICollectionData<ICourse> => store.additionalCourseList;

/** Returns course list filter. */
const getListFilter = (store: any): IAdditionalCourseListFilter => ListFilterBusinessStore.selectors.getListFilter(store, ADDITIONAL_COURSE_LIST_FILTER);

// -
// -------------------- Actions

const Actions = {
  ADDITIONAL_COURSE_LIST_FETCH: 'ADDITIONAL_COURSE_LIST_FETCH',
  ADDITIONAL_COURSE_LIST_LOAD: 'ADDITIONAL_COURSE_LIST_LOAD',
  ADDITIONAL_COURSE_LIST_CLEAR: 'ADDITIONAL_COURSE_LIST_CLEAR',
};

/** Fetch course list by filter. */
const fetchList = (params: ICollectionFetchPayload<IAdditionalCourseListFilter>): IPayloadAction<ICollectionFetchPayload<IAdditionalCourseListFilter>> => {
  return {
    type: Actions.ADDITIONAL_COURSE_LIST_FETCH,
    payload: params,
  };
};

/** Load course list to store. */
const loadList = (data: ICollectionData<ICourse>): IPayloadAction<ICollectionData<ICourse>> => {
  return {
    type: Actions.ADDITIONAL_COURSE_LIST_LOAD,
    payload: data,
  };
};

/** Clear course list from store. Eg. when leaving list view. */
const clearList = (): ILemonAction => {
  return {
    type: Actions.ADDITIONAL_COURSE_LIST_CLEAR,
  };
};

/** Store course list filter to store. */
const storeListFilter = (listFilter: IAdditionalCourseListFilter): ILemonAction => {
  return ListFilterBusinessStore.actions.storeListFilter(ADDITIONAL_COURSE_LIST_FILTER, listFilter);
};

const clearListFilter = (): ILemonAction => {
  return ListFilterBusinessStore.actions.clearListFilter(ADDITIONAL_COURSE_LIST_FILTER);
};

// -
// -------------------- Side-effects

const fetchAdditionalCourseListEffect = (action$: Observable<IPayloadAction<ICollectionFetchPayload<IAdditionalCourseListFilter>>>, state$: StateObservable<any>) => {
  return action$.pipe(
    filter((action) => {
      return action.type === Actions.ADDITIONAL_COURSE_LIST_FETCH;
    }),

    startGlobalProgress(),

    mergeMap((action) => {
      const payload = action.payload;

      return EntityApiServiceRegistry.getService('PublicCourse')
        .fetchEntityList(payload)
        .pipe(actionThunk(action));
    }),

    stopGlobalProgress(),

    map((data) => {
      return loadList(data);
    }),

    reportCaughtMessage((error: any) => createStaticMessageUserFeedbackError('GENERAL_MESSAGE.GENERAL_FETCH_ERROR')),

    catchError((error: any, o: Observable<any>) => {
      console.error('Error fetching course list', error);
      return o;
    })
  );
};

// -
// -------------------- Reducers

const additionalCourseList = (state: ICollectionData<ICourse> | null = null, action: IPayloadAction<ICollectionData<ICourse>>) => {
  if (action.type === Actions.ADDITIONAL_COURSE_LIST_LOAD) {
    return {
      ...action.payload,
    };
  } else if (action.type === Actions.ADDITIONAL_COURSE_LIST_CLEAR) {
    return null;
  }

  return state;
};

// --
// -------------------- Business Store

export const AdditionalCourseListBusinessStore = {
  actions: {
    fetchList,
    clearList,
    storeListFilter,
    clearListFilter,
  },

  selectors: {
    getList,
    getListFilter,
  },

  effects: {
    fetchAdditionalCourseListEffect,
  },

  reducers: {
    additionalCourseList,
  },
};

// --
// export business store
export default AdditionalCourseListBusinessStore;
